import { useSelector } from 'react-redux';

// project constants
import { appConfigMeta, type AppConfigRoot, type AppConfigData } from '@a1/shared';

/**
 * Gets the name of an entity.
 * @param id Entity id (key)
 * @param config App config
 * @returns Entity name (value) or id
 */
const getEntityName = (id: string, config?: AppConfigData): string =>
  config?.entities?.[id] || appConfigMeta?.entities?.[id as keyof typeof appConfigMeta.entities]?.value || id;

/**
 * Hook that returns the name of an entity without requiring appConfig as a parameter
 * @param id Entity id (key)
 * @returns Entity name (value) or id
 */
const useGetEntityName = (id: string): string => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  return getEntityName(id, appConfig);
};

/**
 * Resolves entity name placeholders (e.g. "{surveyEntityName}") in a communication
 * template name for display. Extend this map as more templates gain placeholders.
 * @param name Raw template name, possibly containing placeholder tokens
 * @param config App config
 * @returns Template name with placeholders resolved
 */
const resolveTemplateName = (name: string | undefined | null, config?: AppConfigData): string => {
  if (!name) {
    return '';
  }

  return name.replace('{surveyEntityName}', getEntityName('survey', config));
};

/**
 * Hook that resolves entity name placeholders in a communication template name
 * without requiring appConfig as a parameter
 * @param name Raw template name, possibly containing placeholder tokens
 * @returns Template name with placeholders resolved
 */
const useResolveTemplateName = (name: string | undefined | null): string => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  return resolveTemplateName(name, appConfig);
};

export { getEntityName, useGetEntityName, resolveTemplateName, useResolveTemplateName };
